/**
 * The written debrief (Can't talk) as taps, on one page: the handler's one
 * line, how it went (three rows), before and after (five words each). Once
 * the three taps are in, the handler's line answers back. The account is
 * written from the steps they ticked; nothing to type, nothing to scroll.
 * What comes out is the same two fields the report always filed.
 */
import { Check } from "lucide-react";
import {
  FEELINGS,
  OUTCOMES,
  answersReady,
  reactionFor,
  type DebriefAnswers,
} from "./debrief-answers";

/** A raised key: lit along its top edge, dark along its bottom, a shadow
 *  under it; pressed, it drops into the shadow. The chosen one sits
 *  pressed, lit gold, like a key held down. */
/** A raised key in the case's light: lit along its top edge, a darker
 *  edge along its bottom, a shadow under it; pressed, it drops into the
 *  shadow. */
const RAISED =
  "border border-[color-mix(in_srgb,var(--ink)_35%,transparent)] bg-[linear-gradient(180deg,#fffdf2_0%,var(--flash)_55%,color-mix(in_srgb,var(--flash)_80%,var(--glow))_100%)] text-[var(--ink)] shadow-[inset_0_1px_0_rgba(255,255,255,.8),inset_0_-3px_0_color-mix(in_srgb,var(--ink)_18%,transparent),0_3px_0_color-mix(in_srgb,var(--ink)_55%,transparent),0_8px_16px_rgba(0,0,0,.2)] hover:brightness-[1.03] active:translate-y-[3px] active:shadow-[inset_0_1px_0_rgba(255,255,255,.8),inset_0_-3px_0_color-mix(in_srgb,var(--ink)_18%,transparent),0_0_0_transparent,0_3px_8px_rgba(0,0,0,.2)]";
/** Chosen: the key goes dark, gold text, pressed in with a glow. */
const CHOSEN =
  "translate-y-[3px] border border-[#d6a94e]/60 bg-[linear-gradient(180deg,#2e261c_0%,#1a1410_55%,#120e0a_100%)] text-[#f0c674] shadow-[inset_0_1px_0_rgba(255,236,180,.18),inset_0_-2px_0_rgba(0,0,0,.45),0_0_0_transparent,0_0_18px_rgba(214,169,78,.5)]";

/** A line that flies in, word by word, up out of the yellow: the one thing
 *  on the page to look at. */
function FlyIn({
  text,
  size,
  delay = 0,
  className = "",
}: {
  text: string;
  size: string;
  delay?: number;
  className?: string;
}) {
  const words = text.split(/\s+/);
  return (
    <p
      className={`font-extrabold tracking-[-.02em] text-[var(--ink)] text-balance ${size} ${className}`}
    >
      {words.map((w, i) => (
        <span
          key={i}
          style={{ animationDelay: `${delay + i * 70}ms` }}
          className="inline-block animate-[smTapsWord_.55s_cubic-bezier(.2,.8,.2,1)_both]"
        >
          {w}
          {i < words.length - 1 ? "\u00a0" : ""}
        </span>
      ))}
    </p>
  );
}

export function DebriefTaps({
  handler,
  missionName,
  answers,
  onChange,
}: {
  handler: string | null;
  missionName: string;
  answers: DebriefAnswers;
  onChange: (next: DebriefAnswers) => void;
}) {
  const who = handler ?? "Your handler";
  const set = (patch: Partial<DebriefAnswers>) =>
    onChange({ ...answers, ...patch });
  const ready = answersReady(answers);
  // page one: how did it go; page two: before, after, their words
  const page = answers.outcome ? 2 : 1;

  return (
    <div key={page} className="flex flex-col gap-3">
      {page === 1 ? (
        <>
          <div className="px-1 pt-2 pb-4 text-center">
            <div className="font-mono text-[10px] tracking-[.24em] text-[color-mix(in_srgb,var(--ink)_65%,transparent)] uppercase">
              {who}
            </div>
            <FlyIn
              text={`${missionName} is done.`}
              size="mt-3 text-[clamp(26px,8cqw,36px)] leading-[1.1]"
            />
            <FlyIn
              text="How did it go?"
              size="mt-2 text-[clamp(26px,8cqw,36px)] leading-[1.1]"
              delay={450}
            />
          </div>

          {/* how it went: three keys, as wide as their words */}
          <div
            className="flex animate-[smTapsIn_.5s_ease-out_.9s_both] flex-col items-center gap-2.5"
            role="radiogroup"
            aria-label="How did it go"
          >
            {OUTCOMES.map((o) => {
              const on = answers.outcome === o.id;
              return (
                <button
                  key={o.id}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  aria-label={o.label}
                  onClick={() => set({ outcome: o.id })}
                  className={`inline-flex w-[min(100%,280px)] cursor-pointer items-center justify-center gap-2 rounded-full px-6 py-3 text-center text-[16px] font-bold transition-[translate,box-shadow,filter] duration-100 ${
                    on ? CHOSEN : `border border-transparent ${RAISED}`
                  }`}
                >
                  {on ? <Check className="size-4" strokeWidth={3.5} /> : null}
                  {o.label}
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <>
          {/* before and after, each asked big */}
          {(["before", "after"] as const).map((k, n) => (
            <div key={k} className={n ? "mt-3" : ""}>
              <FlyIn
                text={
                  k === "before"
                    ? "How did you feel before the mission?"
                    : "How did you feel after the mission?"
                }
                size="text-[clamp(20px,5.8cqw,25px)] leading-[1.15]"
                delay={n * 500}
                className="mb-2.5 text-center"
              />
              <div
                style={{ animationDelay: `${n * 500 + 350}ms` }}
                className="grid animate-[smTapsIn_.45s_ease-out_both] grid-cols-5 gap-1.5"
                role="radiogroup"
                aria-label={
                  k === "before" ? "How it felt before" : "How it felt after"
                }
              >
                {FEELINGS.map((f) => {
                  const on = answers[k] === f;
                  return (
                    <button
                      key={f}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      onClick={() => set({ [k]: f })}
                      className={`cursor-pointer rounded-[12px] py-3 text-[12.5px] font-bold transition-[translate,box-shadow,filter] duration-100 ${
                        on ? CHOSEN : `border border-transparent ${RAISED}`
                      }`}
                    >
                      {f}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {/* the handler, once the three taps are in */}
          {ready ? (
            <p className="animate-[smTapsIn_.4s_ease-out_both] text-center text-[14px] leading-snug font-semibold text-[var(--ink)]">
              {reactionFor(answers)}
            </p>
          ) : null}

          {/* in their words: the one thing to type */}
          <div
            style={{ animationDelay: "1100ms" }}
            className="animate-[smTapsIn_.45s_ease-out_both]"
          >
            <div className="mb-1 font-mono text-[10px] tracking-[.2em] text-[var(--muted)] uppercase">
              In your words
            </div>
            <textarea
              rows={2}
              value={answers.detail}
              maxLength={240}
              onChange={(e) => set({ detail: e.target.value })}
              placeholder="What happened? A line is enough."
              className="w-full resize-none rounded-[12px] border border-[color-mix(in_srgb,var(--ink)_25%,transparent)] bg-[color-mix(in_srgb,var(--paper)_70%,var(--day))] px-4 py-2.5 text-[15px] text-[var(--ink)] shadow-[inset_0_2px_6px_color-mix(in_srgb,var(--ink)_12%,transparent)] outline-none placeholder:text-[var(--muted)] focus:border-[var(--ink)]"
            />
          </div>
        </>
      )}
      <style>{`
        @keyframes smTapsWord{from{opacity:0;translate:0 .6em;filter:blur(3px)}to{opacity:1;translate:0 0;filter:none}}
        @keyframes smTapsIn{from{opacity:0;translate:0 14px}to{opacity:1;translate:0 0}}
        @media (prefers-reduced-motion:reduce){.sm-taps *{animation:none !important}}
      `}</style>
    </div>
  );
}
